import React, { useState, useEffect, useCallback } from 'react';

// Import components
import Navigation from './components/Navigation';
import FaceScanner from './components/FaceScanner';
import Login from './components/Login';
import MyReports from './components/MyReports';
import AdminDashboard from './components/AdminDashboard';
import EmployeeManagement from './components/EmployeeManagement';

import DailyReport from './components/DailyReport';
import LateReport from './components/LateReport';
import LeaveReport from './components/LeaveReport';
import SummaryReport from './components/SummaryReport';

import OrganizationSettings from './components/OrganizationSettings';
import MyProfile from './components/MyProfile';
import HolidayManagement from './components/HolidayManagement';
import LeaveRequestForm from './components/LeaveRequestForm'; 
import LeaveApproval from './components/LeaveApproval';
import LateRequestForm from './components/LateRequestForm';
import ForgotScanRequestForm from './components/ForgotScanRequestForm';
import LateApproval from './components/LateApproval'; 
import ForgotScanApproval from './components/ForgotScanApproval'; 
import DepartmentManagement from './components/DepartmentManagement'; 
import PermissionLeaveForm from './components/PermissionLeaveForm';
import Logs from './components/Logs';
import PermissionReport from './components/PermissionReport';
import OfficialBusinessManagement from './components/OfficialBusinessManagement';
import OfficialBusinessReport from './components/OfficialBusinessReport';
import AttendanceManager from './components/AttendanceManager';
import ExtraWorkDaysManagement from './components/ExtraWorkDaysManagement';

import SuperAdminDashboard from './components/SuperAdminDashboard'; // เพิ่มบรรทัดนี้

import { urlBase64ToUint8Array } from './utils/helpers';
import { api } from './utils/api'; // [✅ ตรวจสอบ] ให้แน่ใจว่า import api ถูกต้อง

const VAPID_PUBLIC_KEY = 'BNepQzYQsoSMdPHPE7C3M-GIR86EhMr7n52l2yc-gklpB4y3ZYxMZLlvsQ8D7F0sGvqmrnXBf3xt23fA32cesKA'; 

const CURRENT_APP_VERSION = process.env.REACT_APP_VERSION;

const App = () => {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('face-scanner');
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // [✅ ปรับปรุง] ย้ายฟังก์ชัน logout ขึ้นมาด้านบน และใช้ useCallback เพื่อให้เรียกใช้ใน useEffect ได้
  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setUser(null);
    setCurrentPage('face-scanner');
    setShowMobileMenu(false);
  }, []);

  useEffect(() => {
    if (CURRENT_APP_VERSION) {
      const checkVersion = async () => {
        try {
          const response = await fetch('/version.json?t=' + new Date().getTime(), {
            cache: "no-cache" 
          });
          
          if (!response.ok) {
            console.warn('Could not fetch version.json');
            return; 
          }

          const data = await response.json();
          const deployedVersion = data.version;

          if (deployedVersion && deployedVersion !== CURRENT_APP_VERSION) {
            console.log(`New version found: ${deployedVersion}. Current: ${CURRENT_APP_VERSION}. Reloading...`);
            alert('ตรวจพบการอัปเดตเวอร์ชันใหม่แล้ว ระบบจะโหลดหน้าเว็บเพื่อใช้งานเวอร์ชันล่าสุด');
            window.location.reload(true);
          }
        } catch (error) {
          console.error('Error checking for new version:', error);
        }
      };
      
      checkVersion();
    }
    
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        logout(); // [✅ ปรับปรุง] ถ้าข้อมูลพัง ให้ logout ทิ้งเลย
      }
    }
  }, [logout]);

  useEffect(() => {
    if (user && user.employee_id) {
      const registerPush = async () => {
        
        // [✅ ปรับปรุง] ตรวจสอบ Token ให้ชัวร์ก่อนยิง API
        const token = localStorage.getItem('auth_token');
        if (!token) {
           logout();
           return;
        }

        if ('serviceWorker' in navigator && 'PushManager' in window) {
          try {
            console.log('Registering Service Worker for Push...');
            
            const registration = await navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/sw.js`);
            const permission = await Notification.requestPermission();
            
            if (permission === 'granted') {
              console.log('Notification permission granted.');
              
              const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
              });

              await api.savePushSubscription(subscription, user.employee_id);
              console.log('Web Push Subscribed successfully!');
            } else {
              console.log('Notification permission denied.');
            }
          } catch (error) {
            console.error('Error registering push notification:', error);
            
            // [✅ ปรับปรุง] ถ้า Backend ฟ้องว่า 401 (หมดอายุ/ไม่ได้ล็อกอิน) ให้ล้างข้อมูลแล้วเตะออกอัตโนมัติ
            if (error.message && (error.message.includes('401') || error.message.includes('Unauthorized') || error.message.includes('เข้าสู่ระบบ'))) {
               console.warn('เซสชันหมดอายุ ทำการล็อกเอาท์อัตโนมัติ...');
               logout();
            }
          }
        } else {
          console.warn('This browser does not support push notifications.');
        }
      };

      registerPush();
    }
  }, [user, logout]); // [✅ ปรับปรุง] แนบ logout เข้าไปเป็น dependency

  const renderCurrentPage = () => {
    const noAccessComponent = (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">ไม่มีสิทธิ์เข้าถึง</h2>
        <p className="text-gray-600 mb-6">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
        <button
          onClick={() => setCurrentPage('face-scanner')}
          className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition-colors"
        >
          กลับหน้าหลัก
        </button>
      </div>
    );

    const isStaffOrAdmin = user?.is_admin || user?.is_staff;

    switch (currentPage) {
      case 'face-scanner':
        return user ? <FaceScanner /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      
      case 'login':
        return <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      
      case 'my-reports':
        return user ? <MyReports user={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      
      case 'my-profile':
        return user ? <MyProfile user={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;

      case 'leave-request':
        return user ? <LeaveRequestForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;

      case 'late-request':
        return user ? <LateRequestForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;
      
      case 'forgot-scan-request':
        return user ? <ForgotScanRequestForm user={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;

      case 'permission-leave':
        return user ? <PermissionLeaveForm currentUser={user} /> : <Login setUser={setUser} setCurrentPage={setCurrentPage} />;

      case 'holidays':
        return isStaffOrAdmin ? <HolidayManagement /> : noAccessComponent;

      case 'department-management':
        return isStaffOrAdmin ? <DepartmentManagement user={user} /> : noAccessComponent;
        
      case 'leave-approval':
        return isStaffOrAdmin ? <LeaveApproval user={user} /> : noAccessComponent;

      case 'late-approval':
        return isStaffOrAdmin ? <LateApproval user={user} /> : noAccessComponent;
        
      case 'forgot-scan-approval':
        return isStaffOrAdmin ? <ForgotScanApproval user={user} /> : noAccessComponent;

      case 'admin-dashboard':
        return isStaffOrAdmin ? <AdminDashboard setCurrentPage={setCurrentPage} user={user} /> : noAccessComponent;
      
      case 'employees':
        return isStaffOrAdmin ? <EmployeeManagement user={user} /> : noAccessComponent;

      case 'logs':
        return isStaffOrAdmin ? <Logs user={user} /> : noAccessComponent;
      
      case 'daily-report':
        return isStaffOrAdmin ? <DailyReport user={user} /> : noAccessComponent;
        
      case 'late-report':
        return isStaffOrAdmin ? <LateReport user={user} /> : noAccessComponent;
        
      case 'leave-report':
        return isStaffOrAdmin ? <LeaveReport user={user} /> : noAccessComponent;

      case 'permission-report':
        return isStaffOrAdmin ? <PermissionReport user={user} /> : noAccessComponent;
        
      case 'summary-report':
        return isStaffOrAdmin ? <SummaryReport user={user} /> : noAccessComponent;
      
      case 'settings':
        return isStaffOrAdmin ? <OrganizationSettings /> : noAccessComponent;

      case 'super-admin-dashboard':
        return user?.is_super_admin ? <SuperAdminDashboard user={user} /> : noAccessComponent;
      
      case 'official-business':
        return <OfficialBusinessManagement currentUser={user} />;

      case 'official-business-report':
        return <OfficialBusinessReport user={user} />;

      case 'attendance-manager':
        return isStaffOrAdmin ? <AttendanceManager user={user} /> : noAccessComponent;

      case 'extra-work-days':
        return isStaffOrAdmin ? <ExtraWorkDaysManagement /> : noAccessComponent;
      
      default:
        if (isStaffOrAdmin) {
          setCurrentPage('admin-dashboard');
          return <AdminDashboard setCurrentPage={setCurrentPage} user={user} />;
        }
        return <FaceScanner />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation 
        user={user} 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        logout={logout}
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
      />
      
      <main className="container mx-auto px-4 py-8 mt-20">
        {renderCurrentPage()}
      </main>
    </div>
  );
};

export default App;